import _ from 'lodash';

import {
  endsWithLatinCharacter,
  isBasicLink,
  splitBasicLinks,
} from '../../../libs/shared/core/src/helpers/utilities/stringHelpers';
import {
  EmojiTextPart,
  emojiToDefinition,
  isValidEmoji,
  isValidShortcode,
  shortcodeToUrl,
  splitMarkdownEmojis,
} from '../../../srcCommon/helpers/emoji/emojiTextHelpers';
import {EmojiMap} from '../../services/emojis/emojis';
import {splitEmoticonsToEmoji, textPartContainsEmoticons} from './emoticonsTextHelpers';

// TODO: Should this function replace splitMarkdownEmojis()?
export function splitEmojisAndEmoticonsOutsideLinksAndChars(
  text: string,
  validationMap: EmojiMap,
  shouldReplaceEmoticons?: boolean,
  cursorPosition?: number,
  previousSiblingEndsWithLatinCharacter?: boolean,
): EmojiTextPart[] {
  // Find links in this text.
  const linkParts = splitBasicLinks(text);
  return _(linkParts)
    .flatMap((linkPart) => {
      // Do not replace anything inside links.
      if (isBasicLink(linkPart)) {
        return {text: linkPart};
      }

      // Otherwise, look for emojis/emoticons.
      const emojiParts = splitMarkdownEmojis(linkPart);
      return _(emojiParts)
        .flatMap((emojiPart, index) => {
          // Only replace if not after alphanumeric or some special characters
          if (
            (isValidEmoji(emojiPart, validationMap) || isValidShortcode(emojiPart, validationMap)) &&
            (index > 1 ||
              (index === 1 && (emojiParts[0] !== '' || !previousSiblingEndsWithLatinCharacter))) &&
            !endsWithLatinCharacter(emojiParts[index - 1])
          ) {
            return {
              text: emojiPart,
              emojiAndSkinTone: emojiToDefinition(emojiPart, validationMap),
            };
          }

          if (!shouldReplaceEmoticons || !textPartContainsEmoticons(emojiPart, validationMap)) {
            return {text: emojiPart};
          }

          return splitEmoticonsToEmoji(emojiPart, text, cursorPosition);
        })
        .value();
    })
    .value();
}

export function isInsideOfCodeBlock(element: Element, currentNode: Text): boolean {
  let currentParent = currentNode.parentElement;
  while (currentParent && !element.isSameNode(currentParent)) {
    if (currentParent.tagName === 'CODE') {
      return true;
    }

    currentParent = currentParent.parentElement;
  }
  return false;
}

/*
 * Customizable transform.
 */

type EmojiTransformer<T> = (key: string, emojiUrl: string, raw: string) => T;

/** Transform emoji in a text using the provided transformer. */
export function transformEmojis<T>(
  text: string,
  validationMap: EmojiMap,
  transformer: EmojiTransformer<T>,
  previousToken?: string,
) {
  const previousTokenEndsWithLatinCharacterOrIsHTMLTag =
    previousToken !== undefined && endsWithLatinCharacter(previousToken ?? '');
  const textParts = splitEmojisAndEmoticonsOutsideLinksAndChars(
    text,
    validationMap,
    true,
    text.length,
    previousTokenEndsWithLatinCharacterOrIsHTMLTag,
  );
  return textParts.map((emojiAndTextParts, index) => {
    // Check if this is an emoji.
    const {text: splitText, emojiAndSkinTone} = emojiAndTextParts;
    if (!emojiAndSkinTone) {
      return splitText;
    }

    // Try to find the image for the matched emoji.
    const emojiImage = shortcodeToUrl(splitText, validationMap);
    if (!emojiImage) {
      return splitText;
    }

    // If one was found, transform.
    return transformer(index.toString(), emojiImage, splitText);
  });
}
